import { Box, Button, Tooltip, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { API_GET, DBRequest, useConfig } from '../../api/api';
import { useTranslation } from 'react-i18next';
import TypeProjectSearch from '../form/form-fields/TypeProjectSearch';
import TypeDateRange from '../form/form-fields/TypeDateRange';
import MoneyLabel from '../label/MoneyLabel';
import { tokens } from '../../global/theme/tokens';
import { renderHours } from '../../scenes/time-tracking/worktime';

const SelectTimeBilling = ({ onSelect, onClose = () => {} }) => {
  const config = useConfig();
  const { t } = useTranslation();

  const [chrData, setChrData] = useState(null);
  const [dataCollection, setDataCollection] = useState({});
  const [selected, setSelected] = useState([]);

  function handleChange(key, data) {
    const newDataCollection = { ...dataCollection, [key]: data };
    setDataCollection(newDataCollection);
    if (!newDataCollection.project) return;

    let params = [];
    if (dataCollection.dateRangeStart)
      params.push(`startDate=${dataCollection.dateRangeStart}`);
    if (dataCollection.dateRangeEnd)
      params.push(`endDate=${dataCollection.dateRangeEnd}`);

    const filter = params.length ? `?${params.join('&')}` : '';

    DBRequest({
      config,
      path: `projects/${newDataCollection.project.id}/wage-presets${filter}`,
      method: API_GET,
      onResponse: (res) => setChrData(res.data),
    });
  }

  function handleSelection(chr) {
    let newSelected = selected;
    if (newSelected.includes(chr)) {
      newSelected = newSelected.filter((el) => el.id != chr.id);
    } else {
      newSelected.push(chr);
    }
    setSelected([...newSelected]);
  }

  function handleSubmit() {
    onSelect({ ...dataCollection, chrs: selected });
  }

  return (
    <Box className="flex flex-col gap-2">
      <Box className="flex justify-center my-2">
        <Typography fontSize={22}>{t('Time Billing')}</Typography>
      </Box>
      <TypeDateRange
        field={{ label: t('Billing period'), key: 'dateRange' }}
        dataCollection={dataCollection}
        updateValidationOnChange={handleChange}
      />
      <TypeProjectSearch
        field={{ label: t('Project'), key: 'project', openOnStart: true }}
        dataCollection={dataCollection}
        updateValidationOnChange={handleChange}
      />
      <Box className="flex flex-col gap-2">
        {chrData && chrData.length > 0 ? (
          chrData.map((chr) => (
            <BillingCHR chr={chr} onSelect={handleSelection} />
          ))
        ) : (
          <Box className="flex justify-center mt-2">
            <Typography>{t('No Calculative Hourly Rates found')}</Typography>
          </Box>
        )}
      </Box>
      <Box className="flex justify-end pt-4">
        <Button
          color="success"
          variant="contained"
          disabled={selected.length == 0}
          onClick={handleSubmit}
        >
          {t('Accept')}
        </Button>
      </Box>
    </Box>
  );
};

const BillingCHR = ({ chr, onSelect }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [selected, setSelected] = useState(false);

  function handleClick() {
    setSelected((selected) => !selected);
    onSelect(chr);
  }

  return (
    <Box
      className="relative flex p-2 items-center justify-between gap-2 w-full"
      onClick={() => handleClick()}
      sx={{
        backgroundColor: selected ? colors.selected : colors.bgInput,
        borderRadius: 4,
        borderLeft: `10px solid ${chr.color}`,
        borderRight: '0px solid transparent',
        //':hover': { backgroundColor: colors.selected },
      }}
    >
      {chr.image && (
        <img
          className="w-[32px] h-[32px] object-contain"
          src={chr.image.temporaryUrl}
        />
      )}
      <Box className="flex flex-col justify-between h-full w-full">
        <Typography fontWeight={600}>{chr.label}</Typography>
        <Typography>
          <MoneyLabel money={chr.wage} />
        </Typography>
      </Box>
      <Box className="flex flex-col">
        <Box className="flex items-center">
          <Tooltip title={t('Open worked time')}>
            <Typography fontWeight={600}>
              {renderHours(
                (chr.workedTimeSeconds - chr.billedTimeSeconds) / 60
              ) + 'h'}
            </Typography>
          </Tooltip>
          <Typography className="px-[3px]">{'/'}</Typography>
          <Tooltip title={t('Open planned time')}>
            <Typography fontWeight={600}>
              {renderHours(
                (chr.plannedTimeSeconds - chr.billedTimeSeconds) / 60
              ) + 'h'}
            </Typography>
          </Tooltip>
          <Typography className="pl-1">
            {`(${Math.round(
              ((chr.workedTimeSeconds - chr.billedTimeSeconds) /
                (chr.plannedTimeSeconds - chr.billedTimeSeconds)) *
                100
            )}%)`}
          </Typography>
        </Box>

        <Box className="flex items-center">
          <Tooltip title={t('Total worked time')}>
            <Typography className="opacity-70">
              {renderHours(chr.workedTimeSeconds / 60) + 'h'}
            </Typography>
          </Tooltip>
          <Typography className="px-[3px]">{'/'}</Typography>
          <Tooltip title={t('Total planned time')}>
            <Typography className="opacity-70">
              {renderHours(chr.plannedTimeSeconds / 60) + 'h'}
            </Typography>
          </Tooltip>
          <Typography className="pl-1 opacity-70">
            {`(${Math.round(
              (chr.workedTimeSeconds / chr.plannedTimeSeconds) * 100
            )}%)`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SelectTimeBilling;
